/**
 * SYR-107 — which element the hypothesis keeps choosing.
 *
 * A structural bias here is invisible one read-out at a time. Nine runs in,
 * three of the five elements had never once been chosen and the tool had a
 * two-answer vocabulary, spotted by eye long after it started.
 *
 * Two reasons it matters beyond looking repetitive: two brands comparing
 * read-outs get the same verdict, and a constraint the public evidence cannot
 * actually see is a coin flip against the bottom-2 resolution rule, which is
 * the ledger the product's credibility rests on.
 *
 * NEVER CHOSEN is the sharper of the two signals. One element dominating can
 * be real, because review corpora skew negative and quality complaints are the
 * most common public signal. An element that is never reachable is a fault in
 * the reasoning or in the evidence available to it.
 *
 * Hardcoded strings and a plain fetch wrapper, matching the rest of the AI Hub
 * panels rather than importing a second pattern into the same tab.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Progress, Space, Tag, Typography, message } from 'antd';
import { PieChartOutlined } from '@ant-design/icons';
import {
    aiAdminApi,
    HypothesisConstraintDistribution,
} from '../../../services/ai-admin-api';

const { Text, Paragraph } = Typography;

export const ConstraintDistributionCard: React.FC<{ jwt: string }> = ({ jwt }) => {
    const [data, setData] = useState<HypothesisConstraintDistribution | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        if (!jwt) return;
        setLoading(true);
        aiAdminApi
            .getHypothesisConstraintDistribution(jwt)
            .then(setData)
            .catch(() => message.error('Failed to load the constraint distribution'))
            .finally(() => setLoading(false));
    }, [jwt]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Card
            title={
                <span>
                    <PieChartOutlined style={{ marginRight: 8 }} />
                    Which element gets chosen
                </span>
            }
            loading={loading}
            style={{ marginTop: 24 }}
        >
            <Paragraph type="secondary">
                The constraint picked across the last {data?.total ?? 0} formed hypotheses.
                An even spread would be about 20% each. Some clustering is expected, because
                review corpora skew negative and quality complaints are the most common
                public signal.
            </Paragraph>

            {data?.skewed ? (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Worth a look"
                    description={
                        data.unusedElements.length ? (
                            <>
                                Never chosen: <strong>{data.unusedElements.join(', ')}</strong>.
                                An element the hypothesis is allowed to pick and never does
                                usually means the public evidence cannot speak to it, so it is
                                being reasoned about in the abstract rather than from evidence.
                            </>
                        ) : (
                            <>
                                One element is over {Math.round((data.threshold ?? 0.4) * 100)}% of
                                recent runs. Two brands comparing read-outs would get the same
                                verdict.
                            </>
                        )
                    }
                />
            ) : null}

            <Space direction="vertical" style={{ width: '100%' }} size={10}>
                {(data?.breakdown ?? []).map((row) => (
                    <div key={row.element}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text strong>{row.element}</Text>
                            <Text type="secondary">
                                {row.count} ({Math.round(row.share * 100)}%)
                            </Text>
                        </div>
                        <Progress
                            percent={Math.round(row.share * 100)}
                            showInfo={false}
                            size="small"
                            // Amber past the threshold: a prompt to look, not a failure.
                            strokeColor={row.share >= (data?.threshold ?? 0.4) ? '#d97706' : '#94A3B8'}
                        />
                    </div>
                ))}

                {(data?.unusedElements ?? []).map((element) => (
                    <div key={element}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text type="secondary">{element}</Text>
                            <Tag color="red">never chosen</Tag>
                        </div>
                        <Progress percent={0} showInfo={false} size="small" />
                    </div>
                ))}
            </Space>
        </Card>
    );
};
